import React, { useContext } from 'react'

import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'

import { Paper } from '@mui/material'
import { makeStyles } from '@mui/styles'

import AutoSizer from 'react-virtualized-auto-sizer'
import { areEqual, FixedSizeList as WindowedList } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'

import { useAllowedTierItems } from '~/src/Flags/Tier'
import { getInfiniteLoaderProps, getListProps, JournalContext } from '~/src/Journal/utils'
import createLogger from '~/src/Lib/Logging'
import { datePropType } from '~/src/Lib/PropTypes'

import Header from './Header'
import getItemRenderer from './Item'
import Loading from './Loading'
import NoEntries from './NoEntries'
import styles from './styles'

export const displayName = 'Journal/List'

const logger = createLogger(displayName)
const useStyles = makeStyles(styles, { name: displayName })
const listContainerStyles = {
  overflowX: 'hidden',
  overflowY: 'auto',
}

const getListKwargs = memoizeOne((topItem, props) => ({ ...props, topItem }))
const ListWrapper = React.memo(props => {
  const { activeRoom, topItem } = useContext(JournalContext)

  return (
    <WindowedList
      {...getListProps(getListKwargs(topItem, props))}
      className="scroller"
      style={listContainerStyles}
    >
      {getItemRenderer({ activeRoom }, areEqual)}
    </WindowedList>
  )
})

const getInfiniteLoaderChildren = memoizeOne(({ adjustHeight, ...props }, { height, ...autoSizerProps }) => (
  memoizeOne(({ onItemsRendered, ref }) => (
    <ListWrapper
      {...props}
      {...autoSizerProps}
      height={height + adjustHeight}
      onItemsRendered={onItemsRendered}
      innerRef={ref}
      style={listContainerStyles}
    />
  ))
))

const getAutoSizerChildren = memoizeOne(props => (
  memoizeOne(autoSizerProps => (
    <InfiniteLoader {...getInfiniteLoaderProps(props)}>
      {getInfiniteLoaderChildren(props, autoSizerProps)}
    </InfiniteLoader>
  ))
))

export const JournalListComponent = ({
  adjustHeight = 0,
  journal,
  loaded,
  topItem = null,
  noHeader = false,
  ...props
}) => {
  const classes = useStyles(props)

  const allowedItems = useAllowedTierItems(journal)

  if (!loaded) return <Loading classes={classes} />
  const HeaderContent = !noHeader ? (
    <Header
      {...props}
      classes={classes}
      topItem={topItem}
      adjustHeight={adjustHeight}
    />
  ) : null

  return (allowedItems?.length > 0 && loaded) ? (
    <Paper data-testid="facility-dashboard-journal-list" square className={classes.journalRoot}>
      {HeaderContent}
      <div className={classes.list}>
        <AutoSizer>{getAutoSizerChildren({
          ...props,
          journal: { ...journal, items: allowedItems },
          topItem,
          adjustHeight,
        })}
        </AutoSizer>
      </div>
    </Paper>
  ) : <NoEntries {...props} journal={{ ...journal, items: allowedItems }} classes={classes}>{HeaderContent}</NoEntries>
}

JournalListComponent.propTypes = {
  adjustHeight: PropTypes.number,
  itemCount: PropTypes.number.isRequired,
  loaded: PropTypes.bool.isRequired,
  noHeader: PropTypes.bool,
  topItem: PropTypes.shape({ uuid: PropTypes.string, timestamp: datePropType }),
}

JournalListComponent.displayName = displayName

export default React.memo(JournalListComponent)
